import type { CommentNotificationDTO, ItemNotificationDTO, NotificationDTO } from '@contracts/activity';
import type { Id } from '@contracts/common';

/**
 * Grouping rule A of spec §9, in the browser only: assignment notices from the same person about the same big
 * job read as one row — "Minh Tuấn giao bạn 12 việc trong “…”". The server keeps one row per notification, and so
 * does the bell's count.
 *
 * Rows are taken newest first, and each candidate is compared with the NEWEST notice of the group it would join,
 * never with the row just before it: otherwise a steady trickle one notice every 9 minutes would chain into a
 * group spanning hours. A row written before the snapshot carried `actorId`/`rootId`/`rootTitle` is always alone, and
 * so is a pending AI action (`assistant_pending`, S2 D71): it keeps its own place by time.
 *
 * S9 §7.1: an assignment the reader can still answer from the bell (`delegation.actionable`) is always alone — its
 * buttons belong to that one card. S9 §7.3 (D84): unread `comment` rows by the same person on the same card, with no
 * time window, read as one entry "{tên} bình luận {n} lần trên {title}" at the newest member; a read comment, and a
 * lone unread one, stay single.
 */

export type InboxGroup = {
  kind: 'group';
  /** The newest member's id: stable while the group only grows backwards in time. */
  key: Id;
  actorId: Id; rootId: Id; rootTitle: string;
  /** The name frozen into the newest member (ruling T14-a); null on a row written before it was frozen. */
  actorName: string | null;
  /** The newest member's instant. */
  createdAt: string;
  /** True while any member is unread. */
  unread: boolean;
  /** Newest first; at least two. */
  rows: ItemNotificationDTO[];
};

/** D84: two or more unread comments by one person on one card. */
export type InboxCommentGroup = {
  kind: 'comments';
  /** The newest member's id. */
  key: Id;
  actorId: Id; actorName: string | null; itemId: Id;
  /** The card's title as the newest member froze it. */
  title: string;
  createdAt: string;
  /** Always true: only unread comments group. */
  unread: true;
  /** Newest first; at least two. */
  rows: CommentNotificationDTO[];
};

export type InboxEntry = { kind: 'single'; row: NotificationDTO } | InboxGroup | InboxCommentGroup;

/** How far a notice may be from the newest one of its group, inclusive. */
export const GROUP_WINDOW_MS = 10 * 60_000;

const groupable = (row: NotificationDTO): row is ItemNotificationDTO =>
  row.kind === 'assignment' && row.actorId != null && row.rootId != null && row.rootTitle != null
  && row.delegation?.actionable !== true;

const commentGroupable = (row: NotificationDTO): row is CommentNotificationDTO =>
  row.kind === 'comment' && row.readAt === null && row.actorId != null;

const mayJoin = (newer: ItemNotificationDTO, newestInGroup: ItemNotificationDTO): boolean =>
  newer.kind === 'assignment' && newestInGroup.kind === 'assignment'
  && newer.actorId != null && newer.actorId === newestInGroup.actorId
  && newer.rootId != null && newer.rootId === newestInGroup.rootId
  && Math.abs(Date.parse(newer.createdAt) - Date.parse(newestInGroup.createdAt)) <= GROUP_WINDOW_MS;

/** Newest first; a tie on the instant falls back to the id, descending, as the inbox itself orders. */
const newestFirst = (a: NotificationDTO, b: NotificationDTO): number =>
  Date.parse(b.createdAt) - Date.parse(a.createdAt) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

export function groupInbox(rows: readonly NotificationDTO[]): InboxEntry[] {
  const sorted = [...rows].sort(newestFirst);
  // Each slot is one displayed entry, placed where its newest member is: a row that never groups, or a would-be group.
  type Slot = NotificationDTO | { assignments: ItemNotificationDTO[] } | { comments: CommentNotificationDTO[] };
  const slots: Slot[] = [];
  // The open group per actor + root: only the latest one can still take older rows.
  const open = new Map<string, ItemNotificationDTO[]>();
  // D84: one comment group per author + card, no window.
  const threads = new Map<string, CommentNotificationDTO[]>();
  for (const row of sorted) {
    if (commentGroupable(row)) {
      const key = `${row.actorId}\u0000${row.itemId}`;
      const thread = threads.get(key);
      if (thread !== undefined) {
        thread.push(row);
        continue;
      }
      const fresh = [row];
      threads.set(key, fresh);
      slots.push({ comments: fresh });
      continue;
    }
    if (!groupable(row)) {
      slots.push(row);
      continue;
    }
    const key = `${row.actorId}\u0000${row.rootId}`;
    const current = open.get(key);
    if (current !== undefined && mayJoin(row, current[0]!)) {
      current.push(row);
      continue;
    }
    const fresh = [row];
    open.set(key, fresh);
    slots.push({ assignments: fresh });
  }
  return slots.map((slot): InboxEntry => {
    if ('comments' in slot) {
      const members = slot.comments;
      const head = members[0]!;
      if (members.length < 2) return { kind: 'single', row: head };
      return {
        kind: 'comments', key: head.id, actorId: head.actorId!, actorName: head.actorName ?? null, itemId: head.itemId,
        title: head.title, createdAt: head.createdAt, unread: true, rows: members,
      };
    }
    if (!('assignments' in slot)) return { kind: 'single', row: slot };
    const members = slot.assignments;
    const head = members[0]!;
    if (members.length < 2) return { kind: 'single', row: head };
    return {
      kind: 'group', key: head.id, actorId: head.actorId!, actorName: head.actorName ?? null, rootId: head.rootId!,
      rootTitle: head.rootTitle!, createdAt: head.createdAt, unread: members.some((m) => m.readAt === null), rows: members,
    };
  });
}
